import { Badge } from '@/components/ui/badge';
import type { KycStatus } from '../types';

/**
 * KYC status as a badge.
 *
 * Rebased onto the shared `Badge` so vendor status reads the same as every
 * other state in the console — the hand-rolled version was a bordered pill at a
 * different radius and weight, which made the Status column look like it came
 * from a different application than the one beside it.
 *
 * "Documents submitted" is `info` rather than `warning`: nothing is wrong, an
 * admin simply has work to do, and painting the whole queue amber trains people
 * to ignore amber.
 */
const STATUS: Record<
  KycStatus,
  { label: string; tone: 'neutral' | 'info' | 'success' | 'warning' | 'destructive' }
> = {
  PENDING: { label: 'Onboarding', tone: 'neutral' },
  DOCUMENTS_SUBMITTED: { label: 'Awaiting review', tone: 'info' },
  VERIFIED: { label: 'Verified', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'destructive' },
  SUSPENDED: { label: 'Suspended', tone: 'warning' },
};

export function StatusBadge({ status }: { status: KycStatus }) {
  const cfg = STATUS[status];
  return (
    <Badge tone={cfg.tone} dot data-testid={`vendor-status-badge-${status}`}>
      {cfg.label}
    </Badge>
  );
}
